// Header do detalhe do cliente (Story 2.6).
// Avatar + nome + status + badges multi-origem (Owl-managed/SaaS-direct)
// + botões Editar, Anonimizar (disabled+title LGPD Fase 2), Cancelar.
// Story 6.10 — badge "Email inválido" + botão Resetar (admin only).

import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Edit3, FileText, MailX, Shield, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { formatBR, formatDocument } from '@control-center/shared';

import { cn } from '@control-center/ui';

import type { ClientEntity } from '../../lib/clients-api';
import { extractErrorMessage } from '../../lib/errors';
import { useAuth } from '../../hooks/use-auth';
import { useResetClientEmailInvalid } from '../../hooks/use-clients';
import { ClientStatusBadge } from './ClientStatusBadge';
import { PillButton } from './PillButton';

export interface ClientHeaderProps {
  client: ClientEntity;
  onEdit: () => void;
  onCancel: () => void;
  isCancelPending?: boolean;
}

export function ClientHeader({
  client,
  onEdit,
  onCancel,
  isCancelPending,
}: ClientHeaderProps): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isCanceled = client.status === 'cancelado';
  const emailInvalidAt = client.emailInvalidAt ?? null;
  const hasEmailInvalid = emailInvalidAt !== null;

  const [resetOpen, setResetOpen] = useState(false);
  const resetMutation = useResetClientEmailInvalid();

  const initials = client.name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Badge multi-origem: calculado no frontend a partir de created_via.
  // Story 8.x vai refinar quando bridges trouxerem dados mais ricos.
  const originBadge =
    client.created_via === 'control_center_admin'
      ? { label: 'Owl-managed', tone: 'good' as const }
      : { label: 'SaaS-direct', tone: 'warn' as const };

  async function handleConfirmReset(): Promise<void> {
    try {
      await resetMutation.mutateAsync(client.id);
      toast.success('Flag resetada — cliente voltará a receber emails.');
      setResetOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao resetar flag.'));
    }
  }

  return (
    <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div className="flex items-start gap-4">
        <span
          aria-hidden
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-muted to-muted-3 text-[14px] font-semibold text-white"
        >
          {initials || '·'}
        </span>
        <div className="flex flex-col gap-2">
          <h1 className="m-0 text-[26px] font-bold leading-tight tracking-[-0.025em] text-ink">
            {client.name}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-[12px] text-muted">
            <ClientStatusBadge status={client.status} />
            <OriginBadge tone={originBadge.tone} label={originBadge.label} />
            {hasEmailInvalid && <EmailInvalidBadge invalidAt={emailInvalidAt} />}
            {client.fiscalDocumentOverride && (
              <FiscalOverrideBadge document={client.fiscalDocumentOverride} />
            )}
            {client.document && (
              <span className="tnum text-muted-2">{formatDocument(client.document)}</span>
            )}
            {client.email && <span className="text-muted-2">{client.email}</span>}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {isAdmin && hasEmailInvalid && (
          <PillButton
            onClick={() => setResetOpen(true)}
            icon={<MailX size={14} strokeWidth={2} />}
            disabled={resetMutation.isPending}
          >
            {resetMutation.isPending ? 'Resetando…' : 'Resetar email'}
          </PillButton>
        )}
        <PillButton
          onClick={onEdit}
          icon={<Edit3 size={14} strokeWidth={2} />}
          disabled={isCanceled}
        >
          Editar
        </PillButton>
        <PillButton
          icon={<Shield size={14} strokeWidth={2} />}
          disabled
          title="Disponível na Fase 2 (LGPD)."
        >
          Anonimizar
        </PillButton>
        <PillButton
          danger
          onClick={onCancel}
          icon={<Trash2 size={14} strokeWidth={2} />}
          disabled={isCanceled || isCancelPending}
        >
          Cancelar
        </PillButton>
      </div>

      <ResetEmailInvalidDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        clientName={client.name}
        onConfirm={handleConfirmReset}
        isPending={resetMutation.isPending}
      />
    </header>
  );
}

function OriginBadge({ tone, label }: { tone: 'good' | 'warn'; label: string }): JSX.Element {
  const styles =
    tone === 'good' ? 'border-good/40 bg-good/10 text-good' : 'border-warn/40 bg-warn/10 text-warn';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-[6px] border px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider ${styles}`}
    >
      {label}
    </span>
  );
}

function FiscalOverrideBadge({ document }: { document: string }): JSX.Element {
  return (
    <span
      title={`NFs emitidas em ${formatDocument(document)} (override DR-036)`}
      className="inline-flex items-center gap-1 rounded-[6px] border border-line bg-panel-3 px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider text-muted"
    >
      <FileText size={11} strokeWidth={2} />
      NF em CNPJ alternativo
    </span>
  );
}

function EmailInvalidBadge({ invalidAt }: { invalidAt: string }): JSX.Element {
  const dateLabel = (() => {
    try {
      return formatBR(invalidAt);
    } catch {
      return invalidAt.slice(0, 10);
    }
  })();
  return (
    <span
      title={`Email marcado inválido em ${dateLabel}. Cliente não recebe mais emails.`}
      className="inline-flex items-center gap-1 rounded-[6px] border border-bad/40 bg-bad/10 px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider text-bad"
    >
      <AlertTriangle size={11} strokeWidth={2} />
      Email inválido
    </span>
  );
}

interface ResetEmailInvalidDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientName: string;
  onConfirm: () => void;
  isPending: boolean;
}

function ResetEmailInvalidDialog({
  open,
  onOpenChange,
  clientName,
  onConfirm,
  isPending,
}: ResetEmailInvalidDialogProps): JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(440px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em] text-ink">
              Resetar flag de email inválido?
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Description className="text-[13px] leading-relaxed text-muted">
            O email de <strong className="text-ink">{clientName}</strong> será considerado válido
            novamente e o cliente voltará a receber notificações por email. Use apenas se você
            confirmou manualmente que o endereço está correto.
          </Dialog.Description>

          <div className="mt-5 flex items-center justify-end gap-2">
            <PillButton type="button" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancelar
            </PillButton>
            <PillButton type="button" solid onClick={onConfirm} disabled={isPending}>
              {isPending ? 'Resetando…' : 'Confirmar reset'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
